/**
 * SnapshotConfigView — Read-only renderer for config snapshot data.
 * Replaces raw JSON with human-readable tables/cards per config type.
 */

import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';

// ─── Labels & formatting ─────────────────────────────────────────────────────

const CONFIG_SECTION_LABELS: Record<string, string> = {
  // kebab-case (legacy)
  'eligibility-rules': 'Elegibilidad',
  'credit-levels': 'Niveles de Credito',
  'interest-rates': 'Tasas de Interes',
  'exposure-limits': 'Limites de Exposicion',
  // camelCase
  creditLevels: 'Niveles de Credito',
  interestRates: 'Tasas de Interes',
  exposureLimits: 'Limites de Exposicion',
  // snake_case (actual API keys)
  eligibility_rules: 'Elegibilidad',
  credit_levels: 'Niveles de Credito',
  interest_rates: 'Tasas de Interes',
  exposure_limits: 'Limites de Exposicion',
  // short forms (actual API keys)
  eligibility: 'Elegibilidad',
  levels: 'Niveles de Credito',
  rates: 'Tasas de Interes',
  split: 'Distribucion Financiera',
  scores: 'Scoring',
  // operational
  bank_accounts: 'Cuentas Bancarias',
  operational_params: 'Parametros Operativos',
};

const LEVEL_COLORS: Record<string, string> = {
  BRONZE: '#CD7F32',
  SILVER: '#94A3B8',
  GOLD: '#DAA520',
  PLATINUM: '#3B82F6',
};

const fmt = (v: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(v);

const pct = (v: number) => `${(v * 100).toFixed(v % 1 === 0 ? 0 : 1)}%`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Normalize any value to an array. Handles: array, object with .items/.data, single object, null. */
function toArray(data: any): any[] {
  if (Array.isArray(data)) return data;
  if (data == null) return [];
  if (typeof data === 'object') {
    // API might wrap in { items: [...] } or { data: [...] }
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    // Single object — wrap it
    return [data];
  }
  return [];
}

// ─── Sub-renderers ────────────────────────────────────────────────────────────

function EligibilityView({ data }: { data: any }) {
  const rows = toArray(data);
  if (rows.length === 0) return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Regla</th>
            <th className="px-3 py-2 font-medium">Condicion</th>
            <th className="px-3 py-2 font-medium">Valor</th>
            <th className="px-3 py-2 font-medium text-center">Estado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any, i: number) => (
            <tr key={r.sk ?? i} className="border-b border-border/30">
              <td className="px-3 py-2 font-medium text-foreground">{r.rule_name}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {r.condition_field} {r.condition_operator}
              </td>
              <td className="px-3 py-2 font-mono font-semibold text-foreground">{r.condition_value}</td>
              <td className="px-3 py-2 text-center">
                <Badge variant={r.is_active ? 'success' : 'default'}>
                  {r.is_active ? 'Activa' : 'Inactiva'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreditLevelsView({ data }: { data: any }) {
  const rows = toArray(data);
  if (rows.length === 0) return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;
  const sorted = [...rows].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {sorted.map((level: any, i: number) => {
        const color = LEVEL_COLORS[level.level_name] ?? '#666';
        const terms = level.available_terms?.split(',').filter(Boolean).join(', ') ?? '—';
        return (
          <div
            key={level.sk ?? i}
            className={`rounded-lg border bg-card p-3 border-l-[3px] ${!level.is_active ? 'opacity-40' : ''}`}
            style={{ borderLeftColor: color }}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-bold uppercase" style={{ color }}>
                {level.level_name}
              </span>
              <span className="text-[11px] font-semibold" style={{ color }}>
                {level.min_score}–{level.max_score} pts
              </span>
            </div>
            <div className="space-y-1 text-xs">
              <Row label="Credito" value={`${fmt(level.min_credit_mxn)} – ${fmt(level.max_credit_mxn)}`} />
              <Row label="Plazos" value={`${terms} sem`} />
              <Row label="Antiguedad min" value={`${level.min_months} meses`} />
              <Row label="Factor" value={pct(level.score_factor)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InterestRatesView({ data }: { data: any }) {
  const rows = toArray(data);
  if (rows.length === 0) return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;
  const sorted = [...rows].sort((a, b) => (a.term_weeks ?? 0) - (b.term_weeks ?? 0));
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Plazo</th>
            <th className="px-3 py-2 font-medium">Tasa Anual</th>
            <th className="px-3 py-2 font-medium">Metodo</th>
            <th className="px-3 py-2 font-medium">Vigente desde</th>
            <th className="px-3 py-2 font-medium text-center">Estado</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r: any, i: number) => (
            <tr key={r.sk ?? `rate-${i}`} className="border-b border-border/30">
              <td className="px-3 py-2 font-semibold text-foreground">{r.term_weeks} semanas</td>
              <td className="px-3 py-2 font-mono font-bold text-foreground">{pct(r.annual_rate)}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.calculation_method}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.effective_date}</td>
              <td className="px-3 py-2 text-center">
                <Badge variant={r.is_active ? 'success' : 'default'}>
                  {r.is_active ? 'Activa' : 'Inactiva'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SplitView({ data }: { data: any }) {
  const rows = toArray(data);
  const item = rows[0];
  if (!item) return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;

  const total = item.fiado_rate_points + item.partner_rate_points;
  const fiadoPct = total > 0 ? (item.fiado_rate_points / total) * 100 : 0;
  const partnerPct = total > 0 ? (item.partner_rate_points / total) * 100 : 0;

  return (
    <div className="space-y-3">
      {/* Bar */}
      <div className="h-8 w-full overflow-hidden rounded-lg flex">
        <div
          className="flex items-center justify-center bg-primary text-[11px] font-medium text-primary-foreground"
          style={{ width: `${fiadoPct}%` }}
        >
          {fiadoPct > 10 && `${item.fiado_rate_points} pts`}
        </div>
        <div
          className="flex items-center justify-center bg-blue-500 text-[11px] font-medium text-white"
          style={{ width: `${partnerPct}%` }}
        >
          {partnerPct > 10 && `${item.partner_rate_points} pts`}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Fiado" value={`${item.fiado_rate_points} pts`} />
        <Stat label="UNE-Credito" value={`${item.partner_rate_points} pts`} />
        <Stat label="IVA" value={pct(item.iva_rate)} />
      </div>
    </div>
  );
}

function BankAccountsView({ data }: { data: any }) {
  // data = { fiado: { clabe, bank_name }, partner: { clabe, bank_name } }
  const entries = Object.entries(data).filter(([, v]) => v && typeof v === 'object');
  if (entries.length === 0) return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;

  const LABELS: Record<string, string> = { fiado: 'FiadoSA', partner: 'UNE-Credito' };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {entries.map(([key, acct]: [string, any]) => (
        <div key={key} className="rounded-lg border bg-card p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            {LABELS[key] ?? key}
          </p>
          <div className="space-y-1 text-xs">
            <Row label="Banco" value={acct.bank_name ?? '—'} />
            <Row label="CLABE" value={acct.clabe ?? '—'} />
          </div>
        </div>
      ))}
    </div>
  );
}

const FREQ_LABELS: Record<string, string> = {
  DAILY: 'Diario',
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quincenal',
  MONTHLY: 'Mensual',
};

function OperationalParamsView({ data }: { data: any }) {
  if (!data || typeof data !== 'object') return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;

  const freqRanges = data.frequency_ranges ?? {};
  const minDeposits = data.min_deposits ?? {};

  return (
    <div className="space-y-4">
      {/* General params */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {data.weeks_to_default != null && (
          <div>
            <p className="text-[10px] font-medium text-muted-foreground">Semanas para default</p>
            <p className="text-lg font-bold text-foreground">{data.weeks_to_default}</p>
          </div>
        )}
        {data.income_window_days != null && (
          <div>
            <p className="text-[10px] font-medium text-muted-foreground">Ventana de ingresos</p>
            <p className="text-lg font-bold text-foreground">{data.income_window_days} dias</p>
          </div>
        )}
        {data.min_deposit_amount != null && (
          <div>
            <p className="text-[10px] font-medium text-muted-foreground">Deposito minimo</p>
            <p className="text-lg font-bold text-foreground">${data.min_deposit_amount}</p>
          </div>
        )}
      </div>

      {/* Frequency classification table */}
      {Object.keys(freqRanges).length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Clasificacion de frecuencia</p>
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Frecuencia</th>
                  <th className="px-3 py-2 font-medium">Rango (dias)</th>
                  <th className="px-3 py-2 font-medium text-right">Min depositos</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(freqRanges).map((freq) => (
                  <tr key={freq} className="border-b border-border/30">
                    <td className="px-3 py-2 font-medium text-foreground">{FREQ_LABELS[freq] ?? freq}</td>
                    <td className="px-3 py-2 font-mono text-foreground">{freqRanges[freq]}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-foreground">{minDeposits[freq] ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ExposureLimitsView({ data }: { data: any }) {
  const rows = toArray(data);
  if (rows.length === 0) return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos</p>;
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Tipo</th>
            <th className="px-3 py-2 font-medium">Nivel</th>
            <th className="px-3 py-2 font-medium text-right">Max Creditos</th>
            <th className="px-3 py-2 font-medium text-right">Max Monto</th>
            <th className="px-3 py-2 font-medium text-right">Alerta</th>
            <th className="px-3 py-2 font-medium text-right">Bloqueo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any, i: number) => (
            <tr key={r.sk ?? i} className="border-b border-border/30">
              <td className="px-3 py-2 text-muted-foreground">{r.limit_type === 'per_level' ? 'Por nivel' : 'Total'}</td>
              <td className="px-3 py-2 font-medium text-foreground">{r.level_name ?? '—'}</td>
              <td className="px-3 py-2 text-right font-mono text-foreground">{r.max_credits?.toLocaleString('es-MX')}</td>
              <td className="px-3 py-2 text-right font-mono text-foreground">{fmt(r.max_amount_mxn)}</td>
              <td className="px-3 py-2 text-right text-muted-foreground">{pct(r.alert_threshold_pct)}</td>
              <td className="px-3 py-2 text-right text-muted-foreground">{pct(r.block_threshold_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScoresView({ data }: { data: any }) {
  // Scores can be an array of weight/rule items, or a config object with .weights
  const weights: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.weights)
      ? data.weights
      : toArray(data);

  if (weights.length === 0) {
    return <p className="py-4 text-center text-xs text-muted-foreground">Sin datos de scoring</p>;
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Componente</th>
            <th className="px-3 py-2 font-medium text-right">Peso</th>
            <th className="px-3 py-2 font-medium">Descripcion</th>
          </tr>
        </thead>
        <tbody>
          {weights.map((w: any, i: number) => (
            <tr key={w.component ?? i} className="border-b border-border/30">
              <td className="px-3 py-2 font-medium text-foreground">{w.component ?? w.rule_name ?? '—'}</td>
              <td className="px-3 py-2 text-right font-mono font-bold text-foreground">{w.weight ?? w.points ?? '—'}</td>
              <td className="px-3 py-2 text-muted-foreground">{w.description ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-medium text-muted-foreground">{label}</p>
      <p className="text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}

// ─── Fallback for unknown config types ────────────────────────────────────────

function JsonFallback({ data }: { data: any }) {
  return (
    <pre className="text-xs text-muted-foreground overflow-auto max-h-48 p-2 bg-muted/20 rounded">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

// ─── Resolver ─────────────────────────────────────────────────────────────────

function getRenderer(key: string): React.FC<{ data: any }> {
  const normalized = key.toLowerCase();
  if (normalized.includes('eligib')) return EligibilityView;
  if (normalized.includes('level')) return CreditLevelsView;
  if (normalized.includes('interest') || normalized.includes('rate')) return InterestRatesView;
  if (normalized === 'split') return SplitView;
  if (normalized.includes('exposure') || normalized.includes('limit')) return ExposureLimitsView;
  if (normalized.includes('score')) return ScoresView;
  if (normalized.includes('bank') || normalized.includes('account')) return BankAccountsView;
  if (normalized.includes('operational') || normalized.includes('param')) return OperationalParamsView;
  return JsonFallback;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface SnapshotConfigViewProps {
  config: Record<string, any>;
}

function isEmpty(value: any): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    if (Array.isArray(value.items)) return value.items.length === 0;
    if (Array.isArray(value.data)) return value.data.length === 0;
    return Object.keys(value).length === 0;
  }
  return false;
}

export function SnapshotConfigView({ config }: SnapshotConfigViewProps) {
  // Filter out DynamoDB metadata keys (pk, sk, etc.) — only keep config sections
  // Filter out DynamoDB metadata + operational config (split is not a credit policy)
  const META_KEYS = new Set(['pk', 'sk', 'version', 'partner_id', 'created_at', 'created_by', 'description', 'config_type_changed']);
  const entries = Object.entries(config).filter(([key]) => !META_KEYS.has(key));

  if (import.meta.env.DEV) {
    console.log('[SnapshotConfigView] config keys:', Object.keys(config));
    console.log('[SnapshotConfigView] filtered entries:', entries.map(([k, v]) => [k, Array.isArray(v) ? `Array(${v.length})` : typeof v]));
  }

  if (entries.length === 0) {
    return <p className="py-4 text-center text-xs text-muted-foreground">Snapshot sin datos de configuracion</p>;
  }

  const populated = entries.filter(([, value]) => !isEmpty(value));
  const empty = entries.filter(([, value]) => isEmpty(value));

  // If all sections are empty, show the raw data for diagnosis
  if (populated.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Este snapshot no contiene datos de configuracion. Es posible que haya sido creado antes de que se configuraran las politicas.
        </p>
        {import.meta.env.DEV && (
          <pre className="text-[10px] text-muted-foreground overflow-auto max-h-48 p-2 bg-muted/20 rounded">
            {JSON.stringify(config, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {populated.map(([key, value]) => {
        const Renderer = getRenderer(key);
        const title = CONFIG_SECTION_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
        return (
          <Card key={key} title={title}>
            <Renderer data={value} />
          </Card>
        );
      })}

      {empty.length > 0 && (
        <div className="rounded-lg border border-border/50 bg-muted/10 px-4 py-3">
          <p className="text-xs text-muted-foreground">
            Sin datos: {empty.map(([key]) =>
              CONFIG_SECTION_LABELS[key] ?? key.replace(/_/g, ' ')
            ).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}
